/**
 * MODULE: domain/sales-presentations/frontend/utils/markdown.ts
 * PURPOSE: Markdown utilities for presentation slide rendering – strip/parse markdown, split on ---.
 * DEPENDENCIES: none
 * PUBLIC API: stripMarkdown, hasMarkdown, splitMarkdownSlides, normalizeSlidesFromMarkdownBlob, parseMarkdownSegments, parseMarkdownToComponents, SlideLike
 * SIDE_EFFECTS: none
 * AGENT_SAFETY: 🟢 SAFE
 * FILE SIZE: 243 / 250
 * Recent: 2026-02-26 [2.2.144] – • -- artifacts fix; parseMarkdownSegments for rich canvas; parseMarkdownToComponents.
 * See: domain/sales-presentations/README.md | CHANGELOG.md
 */

/** Segment of text with optional bold/italic style for canvas rich rendering. */
export type MarkdownSegment = { text: string; bold?: boolean; italic?: boolean };

/**
 * Strip markdown syntax to plain text for canvas rendering.
 * Canvas can't render rich markdown; this removes **bold**, headers, bullets, etc.
 * Horizontal rules (---) are removed first to avoid "• --" artifacts when bullet conversion runs.
 */
export function stripMarkdown(text: string): string {
  if (typeof text !== 'string' || !text.trim()) return text;
  return text
    .replace(/---+/g, '\n')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/^#+\s*/gm, '')
    .replace(/^[-*]\s*/gm, '• ')
    .replace(/^\d+\.\s*/gm, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .trim();
}

/**
 * Check if a string contains markdown syntax (bold, bullets, headers, etc.).
 */
export function hasMarkdown(text: string): boolean {
  if (typeof text !== 'string') return false;
  return (
    /\*\*[^*]+\*\*|\*[^*]+\*/.test(text) ||
    /^#+\s/m.test(text) ||
    /^[-*]\s/m.test(text) ||
    /---/.test(text) ||
    /`[^`]+`/.test(text)
  );
}

/**
 * Split markdown content on --- into slide chunks.
 * Used when presentation content is stored as a single markdown blob.
 */
export function splitMarkdownSlides(content: string): string[] {
  if (typeof content !== 'string' || !content.trim()) return [];
  const parts = content.split(/\n---+\n|\s---+\s/);
  return parts.map((p) => p.trim()).filter(Boolean);
}

export type SlideLike = {
  id?: string;
  type?: string;
  layout?: string;
  background?: unknown;
  components?: Array<{ id: string; type: string; content: unknown }>;
  title?: string;
  content?: unknown;
};

/**
 * Normalize slides: if a single slide has text content containing ---,
 * split into multiple slides (markdown blob fix).
 */
export function normalizeSlidesFromMarkdownBlob(slides: SlideLike[]): SlideLike[] {
  if (!Array.isArray(slides) || slides.length !== 1) return slides;
  const slide = slides[0];
  if (!slide) return slides;

  const textContent = extractTextContent(slide);
  if (!textContent || !/---/.test(textContent)) return slides;

  const parts = splitMarkdownSlides(textContent);
  if (parts.length <= 1) return slides;

  return parts.map((part, idx) => ({
    ...slide,
    id: `${slide.id ?? 'slide'}-${idx + 1}`,
    components: parseMarkdownToComponents(part, idx),
  }));
}

/**
 * Parse markdown text into structured components (heading, subheading, text-block, bullet-list, numbered-list).
 * Used when normalizing slides from a markdown blob to create richer slide structure.
 */
export function parseMarkdownToComponents(
  text: string,
  slideIdx: number
): Array<{ id: string; type: string; content: unknown }> {
  if (typeof text !== 'string' || !text.trim()) {
    return [{ id: `comp-${slideIdx + 1}`, type: 'text-block', content: '' }];
  }
  const lines = text.split(/\n/);
  const components: Array<{ id: string; type: string; content: unknown }> = [];
  let i = 0;
  let compIdx = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed) {
      i++;
      continue;
    }
    const id = `comp-${slideIdx + 1}-${compIdx + 1}`;
    if (trimmed.startsWith('### ')) {
      components.push({ id, type: 'subheading', content: trimmed.slice(4).trim() });
      i++;
      compIdx++;
      continue;
    }
    if (trimmed.startsWith('## ')) {
      components.push({ id, type: 'subheading', content: trimmed.slice(3).trim() });
      i++;
      compIdx++;
      continue;
    }
    if (trimmed.startsWith('# ')) {
      components.push({ id, type: 'heading', content: trimmed.slice(2).trim() });
      i++;
      compIdx++;
      continue;
    }
    if (/^[-*]\s/.test(trimmed) || /^[-*]$/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length) {
        const ln = lines[i];
        const t = ln.trim();
        if (/^[-*]\s/.test(t)) {
          items.push(t.replace(/^[-*]\s+/, '').trim());
          i++;
        } else if (/^[-*]$/.test(t)) {
          i++;
        } else if (t === '' || /^#+\s/.test(t) || /^\d+\.\s/.test(t)) {
          break;
        } else {
          items.push(t);
          i++;
        }
      }
      if (items.length > 0) {
        components.push({ id, type: 'bullet-list', content: items });
        compIdx++;
      }
      continue;
    }
    if (/^\d+\.\s/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length) {
        const ln = lines[i];
        const t = ln.trim();
        const numMatch = t.match(/^(\d+)\.\s+(.*)/);
        if (numMatch) {
          items.push(numMatch[2].trim());
          i++;
        } else if (t === '' || /^#+\s/.test(t) || /^[-*]\s/.test(t)) {
          break;
        } else {
          items.push(t);
          i++;
        }
      }
      if (items.length > 0) {
        components.push({ id, type: 'numbered-list', content: items });
        compIdx++;
      }
      continue;
    }
    const paraLines: string[] = [trimmed];
    i++;
    while (i < lines.length) {
      const ln = lines[i];
      const t = ln.trim();
      if (t === '' || t.startsWith('#') || /^[-*]\s/.test(t) || /^\d+\.\s/.test(t)) break;
      paraLines.push(ln);
      i++;
    }
    const para = paraLines.join('\n').trim();
    if (para) {
      components.push({ id, type: 'text-block', content: para });
      compIdx++;
    }
  }

  if (components.length === 0) {
    return [{ id: `comp-${slideIdx + 1}`, type: 'text-block', content: text.trim() }];
  }
  return components;
}

/**
 * Parse markdown into segments for canvas rich text rendering (bold, italic).
 * Strips HR (---) and other syntax; returns plain text segments with style hints.
 */
export function parseMarkdownSegments(text: string): MarkdownSegment[] {
  if (typeof text !== 'string' || !text.trim()) return [];
  let s = text
    .replace(/---+/g, '\n')
    .replace(/^[-*]\s*/gm, '• ')
    .replace(/^\d+\.\s*/gm, '')
    .replace(/^#+\s*/gm, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  const segments: MarkdownSegment[] = [];
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*|__([^_]+)__|_([^_]+)_|[^*_]+)/g;
  let m;
  while ((m = re.exec(s)) !== null) {
    const raw = m[1];
    if (raw.startsWith('**') && raw.endsWith('**')) {
      segments.push({ text: m[2] ?? '', bold: true });
    } else if (raw.startsWith('*') && raw.endsWith('*') && raw.length > 1) {
      segments.push({ text: m[3] ?? '', italic: true });
    } else if (raw.startsWith('__') && raw.endsWith('__')) {
      segments.push({ text: m[4] ?? '', bold: true });
    } else if (raw.startsWith('_') && raw.endsWith('_') && raw.length > 1) {
      segments.push({ text: m[5] ?? '', italic: true });
    } else if (raw) {
      segments.push({ text: raw });
    }
  }
  return segments.length > 0 ? segments : [{ text: stripMarkdown(text) }];
}

function extractTextContent(slide: SlideLike): string | null {
  if (Array.isArray(slide.components) && slide.components.length > 0) {
    const first = slide.components[0];
    if (first && typeof first.content === 'string') return first.content;
  }
  if (typeof slide.content === 'string') return slide.content;
  if (typeof slide.title === 'string') return slide.title;
  return null;
}
